import { useEffect, useState } from "react";
import { Button } from "../../../components/button/button";
import { Input } from "../../../components/input/input";
import { Retractable } from "../../../components/retractable/retractable";
import { Text } from "../../../components/text/text";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import AuthAPI from "../../../api/AuthAPI";
import styles from "../_styles/authPage.module.css";

const OTP_COOLDOWN_SECONDS = 120;

const mapApiError = (error, fallback) => {
    return error?.response?.data?.detail || fallback;
};

const parseStateFromMessage = (message) => {
    const lower = String(message || "").toLowerCase();

    if (lower.includes("already enabled") || lower.includes("enabled")) {
        return true;
    }

    if (lower.includes("already disabled") || lower.includes("disabled")) {
        return false;
    }

    return null;
};

const formatTimer = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

export const TwoFactorPage = () => {
    const navigate = useAnimatedNavigate();

    const [challengeId, setChallengeId] = useState("");
    const [code, setCode] = useState("");
    const [action, setAction] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    const [loadingRequest, setLoadingRequest] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);

    const [errorText, setErrorText] = useState("");
    const [twoFaEnabled, setTwoFaEnabled] = useState(null);
    const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);

    useEffect(() => {
        setErrorText("");
    }, [code]);

    useEffect(() => {
        if (otpSecondsLeft <= 0) {
            return;
        }

        const timerId = setInterval(() => {
            setOtpSecondsLeft((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => {
            clearInterval(timerId);
        };
    }, [otpSecondsLeft]);

    useEffect(() => {
        const loadStatus = async () => {
            setLoadingStatus(true);
            setErrorText("");

            try {
                const response = await AuthAPI.get2faStatus();
                setTwoFaEnabled(response.is2faEnabled);
            } catch (error) {
                setErrorText(mapApiError(error, "Не удалось получить текущий статус 2FA"));
            } finally {
                setLoadingStatus(false);
            }
        };

        loadStatus();
    }, []);

    const requestCode = async () => {
        if (typeof twoFaEnabled !== "boolean") {
            setErrorText("Статус 2FA еще не загружен");
            return;
        }

        const nextAction = twoFaEnabled ? "disable" : "enable";

        setLoadingRequest(true);
        setErrorText("");

        try {
            const response = nextAction === "enable"
                ? await AuthAPI.requestEnable2fa()
                : await AuthAPI.requestDisable2fa();

            setAction(nextAction);

            if (typeof response.twoFaEnabled === "boolean") {
                setTwoFaEnabled(response.twoFaEnabled);
            }

            if (response.challengeId) {
                setChallengeId(response.challengeId);
                setCode("");
                const expiresIn = Number(response.expiresIn) || OTP_COOLDOWN_SECONDS;
                setOtpSecondsLeft(Math.min(expiresIn, OTP_COOLDOWN_SECONDS));
                return;
            }

            const inferredState = parseStateFromMessage(response.message);
            if (typeof inferredState === "boolean") {
                setTwoFaEnabled(inferredState);
            }
        } catch (error) {
            setErrorText(mapApiError(error, "Не удалось запросить код подтверждения"));
        } finally {
            setLoadingRequest(false);
        }
    };

    const verifyCode = async (event) => {
        event.preventDefault();

        if (!challengeId) {
            setErrorText("Сначала запросите код подтверждения");
            return;
        }

        const normalizedCode = code.trim();
        if (normalizedCode.length < 4) {
            setErrorText("Введите корректный код из письма");
            return;
        }

        setLoadingVerify(true);
        setErrorText("");

        try {
            if (!action) {
                setErrorText("Сначала запросите код подтверждения");
                return;
            }

            const response = action === "enable"
                ? await AuthAPI.verifyEnable2fa(challengeId, normalizedCode)
                : await AuthAPI.verifyDisable2fa(challengeId, normalizedCode);

            if (typeof response.twoFaEnabled === "boolean") {
                setTwoFaEnabled(response.twoFaEnabled);
            }
            setChallengeId("");
            setCode("");
            setAction(null);
            setOtpSecondsLeft(0);
        } catch (error) {
            setErrorText(mapApiError(error, "Не удалось подтвердить код"));
        } finally {
            setLoadingVerify(false);
        }
    };

    const cancelVerification = () => {
        setChallengeId("");
        setCode("");
        setAction(null);
        setErrorText("");
        setOtpSecondsLeft(0);
    };

    const isVerificationMode = Boolean(challengeId);
    const busy = loadingStatus || loadingRequest || loadingVerify;
    const toggleButtonText = twoFaEnabled ? "Выключить 2FA" : "Включить 2FA";
    const toggleLoadingText = twoFaEnabled ? "Запрашиваем отключение..." : "Запрашиваем включение...";
    const verifyButtonText = action === "disable" ? "Подтвердить выключение" : "Подтвердить включение";
    const isCodeExpired = isVerificationMode && otpSecondsLeft === 0;
    const isResendBlockedByTimer = isVerificationMode && otpSecondsLeft > 0;
    const cancelButtonText = isVerificationMode ? "Отмена ожидания" : "Назад";
    const resendText = isResendBlockedByTimer
        ? `Отправить код заново (${formatTimer(otpSecondsLeft)})`
        : (loadingRequest ? "Отправляем..." : "Отправить код заново");

    const handleCancel = () => {
        if (isVerificationMode) {
            cancelVerification();
            return;
        }

        navigate(-1);
    };

    return (
        <>
            <Text as="div" variant="h3" className={styles.title} size={28} weight={700}>
                Двухфакторная аутентификация
            </Text>

            <form onSubmit={verifyCode}>
                <Retractable active={!isVerificationMode}>
                    <Button
                        text={loadingRequest ? toggleLoadingText : toggleButtonText}
                        onClick={requestCode}
                        disabled={busy || typeof twoFaEnabled !== "boolean"}
                    />
                </Retractable>

                <Retractable active={isVerificationMode}>
                    {!!challengeId &&
                        <div className={styles.form}>
                            <Input
                                value={code}
                                setValue={setCode}
                                label="Код из письма"
                                placeholder="123456"
                                autoComplete="one-time-code"
                                inputMode="numeric"
                            />

                            <Button
                                buttonType="submit"
                                text={loadingVerify ? "Подтверждаем..." : verifyButtonText}
                                disabled={busy || isCodeExpired}
                            />

                            <Button
                                type="secondary"
                                text={resendText}
                                onClick={requestCode}
                                disabled={busy || isResendBlockedByTimer}
                            />
                        </div>}
                </Retractable>
            </form>

            <Button
                type="secondary"
                text={cancelButtonText}
                onClick={handleCancel}
                disabled={busy}
            />

            {
                !!errorText &&
                <Text as="div" align="center" variant="body" className={styles.errorText} size={14} lineHeight="1.3">
                    {errorText}
                </Text>
            }
        </>
    );
};
